<script>
/* eslint no-multi-spaces: 0 */
/* eslint key-spacing    : 0 */

/**
 * Grid module
 * @module components/Grid
 *
 */

import { AgGridVue }      from 'ag-grid-vue'
import { Eved, EventBus } from '@/event-bus'
import { Model, Data }    from './adapters'

// plugins
import '@/core/exporter'
import ColumnSelector     from './column-selector'

// skins
import PerfectScrollbar   from 'perfect-scrollbar'
import 'perfect-scrollbar/css/perfect-scrollbar.css'
import '@/assets/sass/grid-theme.scss'

const ALL_ROWS_SELECTED = 1
const NO_ROWS_SELECTED = 0

export default {
  props: {
    name       : String,  // grid name that shows in the rendered title and in search placeholders
    desc       : String,  // grid description that shows in the rendered subtitle
    endpoint   : String,  // url to retrieve data from (@link module:core/api and @link module:core/api/map)
    permissions: {
      type     : Object,
      default  : function () { return {} }
    },
    screen     : String,  // screen/tab name to retrieve data from (@link module:core/api/map)
    model      : Object,  // column model (either passed or exported by refering/parent module)
    search     : Boolean, // grid has top search bar displayed or not
    views      : Boolean, // grid has multiple views (sets of filtered data) (@link module:components/Grid/views)
    sorting    : Boolean, // columns have sorting enabled
    pinning    : Boolean, // columns can be pinned
    reordering : Boolean, // columns can be reordered
    resizing   : Boolean, // columns can be resized
    visibility : Boolean, // grid has column selector (@link module:components/Grid/column-selector)
    footer     : Boolean, // grid has footer
    legacy     : Boolean,
    layout     : { type: String, // grid layout (page, frame,small, widget)
      default: 'page',
      validator: val => {
        return ['page', 'frame', 'small', 'widget'].indexOf(val) >= 0
      }
    }
  },

  data() {
    return {
      /**
       * Used for top quick search across all columns (@link method:quickSearch)
       */
      searchTerm: '',
      filters: [],
      savedViews: ['one', 'two', 'three', 'four'],
      allRowsSelected: null,
      selectedRows: [],
      unSelectedRows: [],
      permissionsMix:  Object.keys(this.permissions).length === 0 ? this.$user.$.claims || [] : this.permissions
    }
  },

  created() {
    this.initModel()
  },

  beforeMount() {
    this.initGrid()
  },

  mounted() {
    this.initScrollbar()
    this.attachSelectAllRows()
  },

  methods: {
    /**
     * Maps backend model (exported by refering/parent module) to column model
     *
     */
    initModel() {
      const model = this.$parent.model || {}
      this.columnDefs = Model.mixin(model)
    },

    /**
     * Binds grid options and endpoint
     *
     */
    initGrid() {
      this.gridOptions = {
        headerHeight              : 40,                    // header height (px)
        rowHeight                 : 30,                    // row height (px)
        defaultColDef : {
          unSortIcon              : true,                  // always show sort icon
          checkboxSelection       : this.isFirstColumn     // enable checkbox row selector
          /**
           * Selecting all rows is not supported for virtual scrolling
           * (@link method:attachSelectAllRows)
           */
          // headerCheckboxSelection : this.isFirstColumn
        },

        columnDefs                : this.columnDefs,       // column model
        enableFilter              : true,                  // enable column filtering ui
        rowModelType              : 'infinite',            // enable virtual scrolling
        maxBlocksInCache          : 2,                     // cache only 2 sets of data(pages)
        cacheBlockSize            : 100,                   // default of 100 rows per set of data(page)
        enableServerSideSorting   : true,                  // sorting on backend
        enableServerSideFilter    : true,                  // filtering on backend
        datasource                : Data.fetch(this),      // fetch data from backend

        rowSelection              : 'multiple',            // selectable rows
        rowDeselection            : false,
        suppressRowClickSelection : true,
        // onSelectionChanged        : this.onSelectRow,
        onRowSelected             : this.onSelectRow,

        onColumnVisible           : this.onColumnVisibilityChanged,
        // onVirtualColumnsChanged   : this.onColumnsDisplayedChanged,

        onFilterChanged           : this.handleFilters,    // filter callbacks for external filtering
        onModelUpdated            : this.onDataLoaded,     // data loaded callback

        postProcessPopup          : this.handlePopups      // popups behavior
      }
    },

    /**
     * Refresh cache to trigger datasource reload with new @link data:searchTerm
     *
     */
    quickSearch() {
      const quickFilter = {
        key: 'quickSearch',
        label: this.searchTerm
      }

      this.gridOptions.api.refreshInfiniteCache()

      // replace the quick filter in the filters bar
      if (this.searchTerm) {
        this.filters.splice(this.filters.indexOf(quickFilter), 1)
        this.filters.push(quickFilter)
      }
    },

    isFirstColumn(params) {
      const displayedColumns = params.columnApi.getAllDisplayedColumns()
      const thisIsFirstColumn = displayedColumns[0] === params.column

      return thisIsFirstColumn
    },

    onColumnVisibilityChanged(params) {
      const columnHeaderElement = document.querySelector('[col-id="' + params.column.colId + '"]')
      if (!columnHeaderElement) return

      // update select all state
      if (this.allRowsSelected) {
        columnHeaderElement.querySelector('.ag-checkbox-checked').classList.remove('ag-hidden')
        columnHeaderElement.querySelector('.ag-checkbox-unchecked').classList.add('ag-hidden')
      }
    },

    onColumnsDisplayedChanged(params) {
      //
    },

    onSelectAll(checked) {
      // select/deselect all rows and persist selections
      this.allRowsSelected = checked
      this.restoreRowSelections(checked)

      // reset selection
      if (!checked) {
        this.selectedRows = []
        this.unSelectedRows = []
        this.allRowsSelected = this.gridOptions.api.getSelectedRows().length === 0 ? null : this.allRowsSelected
        //
        EventBus.$emit(Eved.GENERIC.UNSELECTED_ALL)
      } else {
        EventBus.$emit(Eved.GENERIC.SELECTED_ALL)
      }
    },

    onSelectRow(event) {
      if (
          (this.selectedRows === ALL_ROWS_SELECTED || this.selectedRows === NO_ROWS_SELECTED) &&
          event.node.selected && this.allRowsSelected ||
          !event.node.selected && this.allRowsSelected === false ||
          !event.node.selected && this.allRowsSelected === null && !this.selectedRows.length
      ) return

      if (event.node.selected) {
        // remove any selected rows from the unselected list
        this.unSelectedRows.length && this.unSelectedRows.splice(this.unSelectedRows.indexOf(event.node.data), 1)
        //
      } else {
        // add unselected rows to the unselected list
        this.selectedRows === ALL_ROWS_SELECTED ? this.unSelectedRows.push(event.node.data) : null
        //
      }

      // reset selected rows list
      this.selectedRows = this.allRowsSelected === true ? ALL_ROWS_SELECTED
                          : this.allRowsSelected === false ? NO_ROWS_SELECTED
                          : this.gridOptions.api.getSelectedRows()

      if (this.selectedRows === NO_ROWS_SELECTED) {
        this.selectedRows = []
      }

      //
      this.selectedRows.length && EventBus.$emit(Eved.GENERIC.SELECTED_ITEM, this.selectedRows)
      this.unSelectedRows.length && EventBus.$emit(Eved.GENERIC.UNSELECTED_ITEM, this.unSelectedRows)
    },

    onDataLoaded() {
      // this.restoreRowSelections(this.allRowsSelected)
    },

    /**
     * Restores selected rows
     *
     */
    restoreRowSelections(checked) {
      this.gridOptions.api.forEachNode((node) => {
        node.setSelected(checked)
      })
    },

    /**
     * Handle popups behavior
     *
     */
    handlePopups(p) {
      // hook transitions
      if (p.column.colDef.closeFilter) {
        p.ePopup.className = 'ag-menu fade out'
        p.ePopup.style.display = 'none'
        p.column.colDef.closeFilter = false
      } else {
        p.ePopup.className = 'ag-menu fade in'
      }

      // autofocus search input
      p.column.gridApi.getFilterInstance(p.column.colDef.field).component.$refs.input.$refs.input.focus()

      // ref to column filter trigger for filter editing
      p.column.colDef.menuTrigger = p.eventSource

      // FIXME: (hide other stuck filter components)
      setTimeout(() => {
        global.jQuery('.ag-menu.fade.in').length > 1 && global.jQuery('.ag-menu.fade.in:first').remove()
      }, 100)
    },

    /**
     * Handle filters from the top filters bar
     *
     */
    handleFilters(e) {
      const appliedFilters = e.api.getFilterModel()
      const filters = []

      Object.keys(appliedFilters).forEach(filter => {
        if (appliedFilters[filter].filter) {
          filters.push({
            key: filter,
            label: appliedFilters[filter].filter
          })
        }
      })

      this.filters = utils.copy(filters)

      // hide filter
      setTimeout(() => {
        var lastFilter = filters.pop()
        lastFilter = lastFilter ? lastFilter.key : null

        if (lastFilter) {
          this.gridOptions.columnApi.getColumn(lastFilter).colDef.closeFilter = true
          this.gridOptions.columnApi.getColumn(lastFilter).colDef.menuTrigger.click()
        }
      }, 500)
    },

    /**
     * Edit a filter (trigger corresponding filter)
     *
     */
    editFilter(filter) {
      if (filter.key === 'quickSearch') {
        const input = this.$refs.quickSearch.$refs.input
        input.focus()
        input.setSelectionRange(0, input.value.length)
      } else {
        this.gridOptions.columnApi.getColumn(filter.key).colDef.menuTrigger.click()
      }
    },

    /**
     * Remove individual filters
     *
     */
    removeFilter(filter) {
      // remove the quick search filter
      if (filter.key === 'quickSearch') {
        this.filters.splice(this.filters.indexOf(filter), 1)
        this.searchTerm = ''
        this.quickSearch()
      } else {
        const filterComponent = this.gridOptions.api.getFilterInstance(filter.key)

        // remove column filter
        filterComponent.setModel(null)

        // apply filters
        this.gridOptions.api.onFilterChanged()

        // remove from list
        this.filters.splice(this.filters.indexOf(filter), 1)
      }
    },

    /**
     * Removes all filters
     *
     */
    removeFilters() {
      this.gridOptions.api.setFilterModel(null)
    },

    /**
     * Binds PerfectScrollbar to viewport
     *
     */
    initScrollbar() {
      const viewport = this.$el.querySelector('.ag-body-viewport')

      this.ps = new PerfectScrollbar(viewport, {
        minScrollbarLength: 20
      })
      // viewport.addEventListener('wheel', (e) => e.preventDefault())
    },

    /**
     * Enables selecting all rows
     *
     */
    attachSelectAllRows() {
      document.querySelector('.ag-header-container').onclick = (event) => {
        if (~event.target.className.indexOf('-checkbox-')) {
          const toggle = event.target.className.indexOf('unchecked') > -1
          this.onSelectAll(toggle)
        }
      }
    },

    /**
     * Exporter
     *
     */
    exportList() {
      EventBus.$emit(Eved.GENERIC.EXPORT, this.selectedRows)
      this.gridOptions.api.exportDataAsCsv()
    }
  },

  components: {
    AgGridVue,
    ColumnSelector
  }
}

</script>

<template lang="pug">
.content.grid(:class='"layout-" + layout')
  
  //- ****** GRID/PAGE TITLE
  .title(v-if='name')
    ed-title-nav(:title='name', :subtitle='desc')
  
  //- ****** SEARCH BAR (QUICK SEARCH)
  .search.bar
    .container
      .row
        .col-md-12
          .row
            .col-md-6
              ed-input.search-box(v-if='search'
                v-model='searchTerm'
                @change='quickSearch'
                :placeholder='$t("Search in") + " " + name'
                search
                ref='quickSearch'
                )
            .col-md-6.actions-bar(v-if='permissionsMix[$root.currentModule]')
              ed-button.btn.btn-actionbar(v-if='"export" in permissionsMix[$root.currentModule]' @click='exportList')
                | Export
              span
                ed-button.btn.btn-actionbar(
                  v-for='(permission, key) in permissionsMix[$root.currentModule]'
                  :key='key'
                  :to='key'
                  v-if='(permission === null && selectedRows.length || permission) && key != "export"'
                ) {{key}}
                  
  
  //- ****** FILTERS BAR
  .filter.bar
    .container
      .row
        .col-md-12
            ed-select.inline(:options='savedViews')
            i.separator(v-if='filters.length')
            ul.filters
              li.filter(v-for='filter in filters')
                i.fas.fa-times-circle.del-icon(@click='removeFilter(filter)')
                a.filter(href='#' @click.prevent='editFilter(filter)') {{filter.label}}
              li(v-if='filters.length')
                a.filter.clear(href='#' @click.prevent='removeFilters') clear all
  .row.grid
    .col-md-12
      //- ****** COLUMNS SELECTOR
      column-selector(:columns='this.columnDefs' :api='gridOptions.columnApi')
      
      //- ****** DATA GRID
      ag-grid-vue.ag-fresh(:endpoint='endpoint' style='width: 100%; height: 350px;',
        :gridOptions='gridOptions'
        :enableColResize='resizing'
        :class='{ "all-rows-selected": !!allRowsSelected }'
        )
</template>

<style lang='scss'>
@import "~@/assets/sass/_variables.scss";
@import "~@/assets/sass/font-awesome/_variables.scss";
@import "~@/assets/sass/_form_fields.scss";

.ag-body-viewport {
  position: relative; // required for perfect-scrollbar
}

body .dawn-modal-container .grid.content.layout-page {
  > .title,
  > .filter.bar,
  > .search.bar {
    margin-left: auto;
    margin-right: auto;
  }
  > .title {
    .dawn-title-nav,
    .container {
      padding: 0;
    }
  }
}

.content.grid {
  .row.grid {
    margin-top: -1px;
  }

  .separator {
    color: $background-light;
    padding-left: 10px;
    padding-right: 10px;
    &:before {
      content: '|';
    }
  }
  
  // TITLE NAV
  .title {
    margin-left: -15px;
    margin-right: -15px;
  }

  // LAYOUTS
  &.layout {
    
    &-page {
      @at-root .theme.skin & {
        padding-top: 0;
      }

      .search.bar {
        // margin-top: 2px;
        background: linear-gradient(darken($background-lightest, 8%), $background-lightest 10%);
        //
        margin-left: -15px;
        margin-right: -15px;
      }
      .search-box {
        margin-top: 10px;
        margin-bottom: 10px;
      }
      .filter.bar {
        background: $background-lightest;
        border-top: 1px solid darken($background-light, 2%);
        border-bottom: 1px solid darken($background-light, 5%);
        //
        margin-left: -15px;
        margin-right: -15px;

        padding-top: 12px;
        padding-bottom: 12px;
      }
    }

    @at-root .dawn-modal-container .grid.layout-page,
    &-small {
      .search.bar {
        margin-top: 5px;
        background: $canvas;
        border: 1px solid $background-light;
      }
      .search-box {
        margin-top: 0;
        padding-bottom: 0;
        
        .form-control {
          font-size: $font-size-small;
          @include placeholder {
            font-size: $font-size-small;
            color: $background;
          }
          &+.search-icon {
            font-size: $font-size-small;
          }
        }
      }
      .filter.bar {
        background: $canvas;
        border-top: 1px solid $background;
      }
    }

  }

  // SEARCH BAR
  .search-box, .search-box .form-control {
    font-size: $font-size-large;
    &, &:focus {
      border: none;
    }

    @include placeholder {
      font-size: $font-size-large;
      color: $background;
    }
    &+.search-icon {
      font-size: $font-size-large;
    }
  }

  // ACTIONS
  .actions-bar {
    text-align: right;
    line-height: 3em;
  }

  // FILTERS
  ul.filters {
    display: inline-block;
    margin-bottom: 0;
    font-size: $font-size-small;

    &, li {
      list-style-type: none;
      padding: 0;

      li {
        display: inline-block;
        margin-right: 1em;

        .filter {
          font-size: 0.95em;
          text-decoration: underline;

          &.clear {
            text-decoration: none;
          }
        }

        .del-icon {
          color: $primary-04;
          margin-right: 7px;
          vertical-align: middle;
        }
      }
    }
  }

  // COLUMN HEADERS
  .ag-fresh .ag-ltr .ag-header-cell {
    padding-top: 12px;
    padding-left: 0px;
    font-size: $font-size-small;
    transform: rotate(.03deg); // soften font a bit
    border: 0;
    text-indent: -1px;

    .ag-cell-label-container {
      .ag-header-cell-text {
        padding-left: 9px;
      }
      .ag-header-cell-menu-button:hover + .ag-header-cell-label .ag-header-cell-text {
        text-decoration: underline;
      }
    }

    .ag-header-cell-menu-button {
      // trigger menu on clicking on entire cell
      // hide default menu icon
      position: absolute;
      width: 100%;
      cursor: pointer;
      transition: none !important; // disabled to play nice with font smoothing

      .ag-icon-menu {
        visibility: hidden;
      }
    }
  }

  // DROPDOWN FILTERS
  .ag-fresh {
    .ag-root {
      border-top-color: $background;
    }
    .ag-header {
      border-bottom-color: $background;

    }

    .ag-menu {
      margin-top: -32px;
      box-shadow: $box-shadow;
      z-index: 9999;
    }
    .ag-menu, .ag-filter {
      background: $canvas;
      //border-color: $gray-light;
      border-color: #ddd;
      border-radius: 0 0 $border-radius-large $border-radius-large;
      transition: opacity .3s ease-in-out;
      opacity: 0;

      &.fade.in,
      &.fade.in .ag-filter {
        opacity: 1;
      }

      @at-root .column.selector,
      .ag-filter {
        padding: 7px 8px 10px;

        .column-title {
          font-weight: 700;
          font-size: $font-size-small;
          transform: rotate(.03deg); //soften font a bit
          margin-top: 5px;
        }
        @at-root .column.selector .column-title {
          line-height: normal;
          font-size: $font-size-xsmall;
          text-transform: uppercase;
        }

        .ag-filter-select {
          margin: 0;
          display: none;
        }
        .ag-filter-filter {
          border: none;
        }
        .ag-filter-apply-panel {
          padding: 0;
          border: none;
          margin-top: 5px;
        }
      }

      @include placeholder {
        font-size: $font-size-base-sm;
      }

      .form-group {
        margin-top: 0;
        line-height: 1.7rem;

        .theme.skin & .form-control[search][class] {
          font-size: $font-size-base-sm;
          padding-top: 0;
          padding-bottom: 0;
          padding-left: 20px;
          height: 30px;
        }

        .form-control[search]::-webkit-input-placeholder { font-size: $font-size-base-sm; }
        .form-control[search]:-moz-placeholder           { font-size: $font-size-base-sm; }
        .form-control[search]::-moz-placeholder          { font-size: $font-size-base-sm; }
        .form-control[search]:-ms-input-placeholder      { font-size: $font-size-base-sm; }
      }

      @at-root .column.selector .form-group {
        margin-top: 0;
      }
      @at-root .column.selector .btn,
      & .btn {
        margin-top: 0;
        margin-bottom: 0;
      }
    }
  }

}

// ICONS
.ag-icon.ag-icon-filter {
  display: none;
  @include fa-icon($fa-var-filter);
}
.ag-icon.ag-icon-asc {
  @include fa-icon($fa-var-sort-up);
}
.ag-icon.ag-icon-desc {
  @include fa-icon($fa-var-sort-down);
}
.ag-icon.ag-icon-none {
  @include fa-icon($fa-var-sort);
}

.ag-icon.ag-icon-checkbox {
  &-unchecked {
    background-image: none;
    @include form-field;
    @include dawn-checkbox;
  }
  &-checked {
    background-image: none;
    @include form-field;
    @include dawn-checkbox;
    opacity: 1;
    color: $border;
  }
}

// Show selectAll on first column
.ag-fresh:not(.all-rows-selected) .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-checked,
.ag-fresh.all-rows-selected .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-unchecked {
  display: none !important;
}
.ag-fresh:not(.all-rows-selected) .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-unchecked,
.ag-fresh.all-rows-selected .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-checked.ag-hidden {
  display: inline-block !important;
}

.ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden,
.ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden {
  display: inline-block !important;
  margin-left: 7px;
}
.content.grid .ag-fresh .ag-ltr .ag-header-cell[style*="left: 0"] .ag-header-cell-menu-button,
.content.grid .ag-fresh .ag-ltr .ag-header-cell[style*="left: 0"] .ag-header-cell-menu-button {
  width: calc(100% - 30px);
}

</style>



// WEBPACK FOOTER //
// index.vue?3a04ba52